import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaBookOpen,
  FaGraduationCap,
  FaAward,
  FaArrowRight,
  FaStar,
  FaLightbulb,
  FaUserGraduate,
} from "react-icons/fa";

const divisions = [
  {
    title: "Elementary",
    level: "FOUNDATION LEVEL",
    number: "01",
    icon: <FaBookOpen />,
    description:
      "We build strong foundations in literacy, numeracy, creativity, and critical thinking. Our nurturing environment encourages curiosity, confidence, good character, and a lifelong love of learning.",
    features: ["Reading & Writing", "Numeracy Skills", "Creative Learning"],
  },
  {
    title: "Junior High",
    level: "DEVELOPMENT LEVEL",
    number: "02",
    icon: <FaLightbulb />,
    description:
      "Students strengthen their academic abilities, develop independent thinking, and discover their talents. We encourage discipline, problem-solving, collaboration, and responsible decision-making.",
    features: ["Academic Growth", "Critical Thinking", "Personal Development"],
  },
  {
    title: "Senior High",
    level: "PREPARATION LEVEL",
    number: "03",
    icon: <FaGraduationCap />,
    description:
      "We prepare students for higher education, career opportunities, and responsible leadership through rigorous academics, practical skills, and a commitment to excellence.",
    features: ["Advanced Studies", "Career Preparation", "Leadership Skills"],
  },
];

const DivisionPage = () => {
  useEffect(() => {
    AOS.init({
      duration: 850,
      once: true,
      easing: "ease-out-cubic",
      offset: 70,
    });
  }, []);

  const styles = `
    .jana-division-section {
      --jd-purple: #54218f;
      --jd-deep-purple: #35105f;
      --jd-purple-light: #f3edfc;
      --jd-gold: #f4c542;
      --jd-gold-dark: #dca91c;
      --jd-text: #29233a;
      --jd-muted: #716b7d;
      position: relative;
      isolation: isolate;
      overflow: hidden;
      padding: 100px 6%;
      background: linear-gradient(180deg, #ffffff 0%, #faf7ff 100%);
      font-family: inherit;
    }

    .jana-division-section *,
    .jana-division-section *::before,
    .jana-division-section *::after {
      box-sizing: border-box;
    }

    .jana-division-section::before {
      content: "";
      position: absolute;
      z-index: -1;
      width: 370px;
      height: 370px;
      top: 35px;
      right: -190px;
      border: 65px solid rgba(84, 33, 143, 0.045);
      border-radius: 50%;
      pointer-events: none;
    }

    .jana-division-section::after {
      content: "";
      position: absolute;
      z-index: -1;
      width: 220px;
      height: 220px;
      bottom: -125px;
      left: -100px;
      background: rgba(244, 197, 66, 0.12);
      border-radius: 50%;
      pointer-events: none;
    }

    .jana-division-header {
      max-width: 790px;
      margin: 0 auto 58px;
      text-align: center;
    }

    .jana-division-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 9px;
      padding: 10px 17px;
      border: 1px solid rgba(84, 33, 143, 0.12);
      border-radius: 50px;
      background: #f2eafb;
      color: var(--jd-purple);
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 1.8px;
      text-transform: uppercase;
    }

    .jana-division-badge svg {
      color: #c18c00;
      font-size: 13px;
    }

    .jana-division-header h2 {
      margin: 23px 0 18px;
      color: var(--jd-deep-purple);
      font-size: clamp(32px, 4.4vw, 52px);
      font-weight: 850;
      line-height: 1.15;
      letter-spacing: -1.6px;
    }

    .jana-division-header h2 span {
      display: block;
      margin-top: 7px;
      color: var(--jd-purple);
    }

    .jana-division-header p {
      max-width: 670px;
      margin: 0 auto;
      color: var(--jd-muted);
      font-size: 15px;
      line-height: 1.95;
    }

    .jana-division-heading-accent {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 7px;
      margin-top: 24px;
    }

    .jana-division-heading-accent span {
      display: block;
      height: 4px;
      border-radius: 10px;
      background: var(--jd-gold);
    }

    .jana-division-heading-accent span:first-child {
      width: 27px;
      opacity: 0.55;
    }

    .jana-division-heading-accent span:nth-child(2) {
      width: 53px;
      background: var(--jd-purple);
    }

    .jana-division-heading-accent span:last-child {
      width: 27px;
      opacity: 0.55;
    }

    .jana-division-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 26px;
      max-width: 1240px;
      margin: 0 auto;
      align-items: stretch;
    }

    .jana-division-card {
      position: relative;
      display: flex;
      flex-direction: column;
      min-width: 0;
      padding: 32px 27px 28px;
      overflow: hidden;
      border: 1px solid #eee6f7;
      border-radius: 22px;
      background: #ffffff;
      box-shadow: 0 12px 38px rgba(53, 16, 95, 0.055);
      transition:
        transform 0.3s ease,
        box-shadow 0.3s ease,
        border-color 0.3s ease;
    }

    .jana-division-card::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(
        90deg,
        var(--jd-purple),
        var(--jd-gold)
      );
      transform: scaleX(0.3);
      transform-origin: left;
      transition: transform 0.35s ease;
    }

    .jana-division-card:hover {
      transform: translateY(-9px);
      border-color: rgba(84, 33, 143, 0.24);
      box-shadow: 0 22px 48px rgba(53, 16, 95, 0.13);
    }

    .jana-division-card:hover::before {
      transform: scaleX(1);
    }

    .jana-division-card-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 27px;
    }

    .jana-division-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 66px;
      height: 66px;
      flex-shrink: 0;
      border-radius: 19px;
      background: linear-gradient(145deg, #f5effd, #e9dcfa);
      color: var(--jd-purple);
      font-size: 27px;
      transition:
        color 0.3s ease,
        background 0.3s ease,
        transform 0.3s ease;
    }

    .jana-division-card:hover .jana-division-icon {
      background: var(--jd-purple);
      color: var(--jd-gold);
      transform: rotate(-5deg);
    }

    .jana-division-number {
      align-self: flex-start;
      color: #e7dfef;
      font-size: 37px;
      font-weight: 900;
      line-height: 1;
      letter-spacing: -1px;
    }

    .jana-division-level {
      display: inline-block;
      align-self: flex-start;
      margin-bottom: 11px;
      color: #9b7413;
      font-size: 10px;
      font-weight: 850;
      letter-spacing: 1.5px;
    }

    .jana-division-card h3 {
      margin: 0 0 14px;
      color: var(--jd-deep-purple);
      font-size: 24px;
      font-weight: 800;
      letter-spacing: -0.5px;
    }

    .jana-division-description {
      margin: 0;
      color: var(--jd-muted);
      font-size: 13.5px;
      line-height: 1.9;
    }

    .jana-division-features {
      display: grid;
      gap: 12px;
      margin: 25px 0 27px;
      padding: 20px 0 0;
      border-top: 1px solid #f0eaf6;
      list-style: none;
    }

    .jana-division-features li {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #51475f;
      font-size: 12.5px;
      font-weight: 650;
    }

    .jana-division-features li svg {
      flex-shrink: 0;
      color: #b58917;
      font-size: 14px;
    }

    .jana-division-card-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-top: auto;
      padding-top: 19px;
      border-top: 1px solid #f0eaf6;
    }

    .jana-division-card-bottom span {
      color: var(--jd-purple);
      font-size: 12px;
      font-weight: 800;
    }

    .jana-division-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 37px;
      height: 37px;
      flex-shrink: 0;
      border-radius: 50%;
      background: var(--jd-purple);
      color: #ffffff;
      font-size: 13px;
      transition:
        background 0.3s ease,
        color 0.3s ease,
        transform 0.3s ease;
    }

    .jana-division-card:hover .jana-division-arrow {
      background: var(--jd-gold);
      color: var(--jd-deep-purple);
      transform: translateX(3px);
    }

    .jana-division-bottom-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 25px;
      max-width: 1240px;
      margin: 38px auto 0;
      padding: 26px 30px;
      border-radius: 18px;
      background: linear-gradient(115deg, #35105f, #54218f);
      box-shadow: 0 15px 35px rgba(53, 16, 95, 0.14);
    }

    .jana-division-banner-copy {
      display: flex;
      align-items: center;
      gap: 17px;
      min-width: 0;
    }

    .jana-division-banner-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 49px;
      height: 49px;
      flex-shrink: 0;
      border-radius: 15px;
      background: rgba(244, 197, 66, 0.15);
      color: var(--jd-gold);
      font-size: 22px;
    }

    .jana-division-banner-copy h4 {
      margin: 0 0 5px;
      color: #ffffff;
      font-size: 16px;
      font-weight: 800;
    }

    .jana-division-banner-copy p {
      margin: 0;
      color: rgba(255, 255, 255, 0.75);
      font-size: 12.5px;
      line-height: 1.7;
    }

    .jana-division-banner-link {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      min-height: 46px;
      padding: 0 19px;
      flex-shrink: 0;
      border: 0;
      border-radius: 10px;
      background: var(--jd-gold);
      color: #35105f;
      font-size: 12px;
      font-weight: 850;
      text-decoration: none;
      transition:
        background 0.25s ease,
        transform 0.25s ease;
    }

    .jana-division-banner-link:hover {
      background: #ffda68;
      color: #35105f;
      transform: translateY(-2px);
    }

    @media (max-width: 1050px) {
      .jana-division-section {
        padding: 80px 5%;
      }

      .jana-division-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 20px;
      }

      .jana-division-card:last-child {
        grid-column: 1 / -1;
      }
    }

    @media (max-width: 650px) {
      .jana-division-section {
        padding: 65px 5%;
      }

      .jana-division-header {
        margin-bottom: 38px;
      }

      .jana-division-header h2 {
        font-size: 33px;
        letter-spacing: -1px;
      }

      .jana-division-header p {
        font-size: 13.5px;
      }

      .jana-division-grid {
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }

      .jana-division-card:last-child {
        grid-column: auto;
      }

      .jana-division-card {
        padding: 26px 23px 24px;
      }

      .jana-division-bottom-banner {
        align-items: stretch;
        flex-direction: column;
        padding: 24px 21px;
      }

      .jana-division-banner-link {
        width: 100%;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .jana-division-card,
      .jana-division-card::before,
      .jana-division-icon,
      .jana-division-arrow,
      .jana-division-banner-link {
        transition: none !important;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <section
        className="jana-division-section"
        aria-labelledby="jana-division-heading"
      >
        <header className="jana-division-header" data-aos="fade-up">
          <span className="jana-division-badge">
            <FaStar />
            Academic Programs
          </span>

          <h2 id="jana-division-heading">
            Excellence at Every Stage
            <span>of the Learning Journey</span>
          </h2>

          <p>
            Every stage of education matters. Our academic divisions
            provide the knowledge, practical skills, and strong values
            students need to grow with confidence and prepare for a
            brighter future.
          </p>

          <div
            className="jana-division-heading-accent"
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
          </div>
        </header>

        <div className="jana-division-grid">
          {divisions.map((division, index) => (
            <article
              className="jana-division-card"
              key={division.title}
              data-aos="fade-up"
              data-aos-delay={index * 140}
            >
              <div className="jana-division-card-top">
                <div className="jana-division-icon" aria-hidden="true">
                  {division.icon}
                </div>

                <span className="jana-division-number">
                  {division.number}
                </span>
              </div>

              <span className="jana-division-level">
                {division.level}
              </span>

              <h3>{division.title}</h3>

              <p className="jana-division-description">
                {division.description}
              </p>

              <ul className="jana-division-features">
                {division.features.map((feature) => (
                  <li key={feature}>
                    <FaAward aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="jana-division-card-bottom">
                <span>Building Brighter Futures</span>

                <span
                  className="jana-division-arrow"
                  aria-hidden="true"
                >
                  <FaArrowRight />
                </span>
              </div>
            </article>
          ))}
        </div>

        <div
          className="jana-division-bottom-banner"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div className="jana-division-banner-copy">
            <div className="jana-division-banner-icon">
              <FaUserGraduate />
            </div>

            <div>
              <h4>Every Learner Deserves the Opportunity to Excel</h4>
              <p>
                Discover an educational environment that encourages
                growth, achievement, and lifelong success.
              </p>
            </div>
          </div>

          <a href="#about" className="jana-division-banner-link">
            Discover Our School
            <FaArrowRight />
          </a>
        </div>
      </section>
    </>
  );
};

export default DivisionPage;
